'use client'

import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import * as CC from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

// Cookie consent banner (RODO / Prawo komunikacji elektronicznej art. 175).
// Two categories: "necessary" (always on, no consent needed) and "analytics"
// (opt-in) which gates OpenObserve RUM + session replay. The lib persists the
// choice in its own `cc_cookie` and re-fires onConsent on every load, so
// returning visitors who accepted analytics get RUM re-initialized here — no
// separate bootstrap needed.
//
// RumLoader is loaded via next/dynamic(ssr:false) and only rendered once
// consent is granted, so its whole import graph (the OpenObserve SDKs, ~190KB)
// is excluded from the page's initial script preload list — a plain
// `import()` inside an always-rendered component gets preloaded eagerly by
// Next regardless of when it's actually called.
const RumLoader = dynamic(() => import('./RumLoader'), { ssr: false })

type Props = { rumAppId?: string; rumClientToken?: string }

export function CookieConsent({ rumAppId, rumClientToken }: Props) {
  const [analyticsGranted, setAnalyticsGranted] = useState(false)

  useEffect(() => {
    const syncAnalytics = () => {
      if (CC.acceptedCategory('analytics')) setAnalyticsGranted(true)
    }
    CC.run({
      guiOptions: {
        consentModal: { layout: 'box', position: 'bottom left' },
        preferencesModal: { layout: 'box' },
      },
      // analytics defaults off; necessary is locked on.
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
          // Best-effort cleanup if consent is later withdrawn.
          autoClear: { cookies: [{ name: /^_oo_/ }] },
        },
      },
      // RUM starts the moment analytics is accepted, and on every subsequent
      // load via onConsent. onChange covers acceptance after the first decision.
      onConsent: syncAnalytics,
      onChange: syncAnalytics,
      language: {
        default: 'pl',
        translations: {
          pl: {
            consentModal: {
              title: 'Szanujemy Twoją prywatność',
              description:
                'Ta strona używa plików cookie niezbędnych do jej działania oraz — za Twoją zgodą — narzędzi analitycznych, które pomagają nam zrozumieć, jak korzystasz z serwisu, i ulepszać go. Możesz zaakceptować wszystkie, odrzucić opcjonalne albo dostosować ustawienia. Szczegóły znajdziesz w Polityce prywatności.',
              acceptAllBtn: 'Akceptuję wszystkie',
              acceptNecessaryBtn: 'Tylko niezbędne',
              showPreferencesBtn: 'Ustawienia',
              footer:
                '<a href="/polityka-prywatnosci">Polityka prywatności</a>',
            },
            preferencesModal: {
              title: 'Ustawienia plików cookie',
              acceptAllBtn: 'Akceptuję wszystkie',
              acceptNecessaryBtn: 'Tylko niezbędne',
              savePreferencesBtn: 'Zapisz wybór',
              closeIconLabel: 'Zamknij',
              sections: [
                {
                  title: 'Korzystanie z plików cookie',
                  description:
                    'Pliki cookie wykorzystujemy, aby zapewnić działanie strony oraz — po uzyskaniu Twojej zgody — do celów analitycznych. Zgodę możesz w każdej chwili zmienić lub wycofać, wracając do tego okna (link „Ustawienia cookies” w stopce).',
                },
                {
                  title: 'Niezbędne',
                  description:
                    'Konieczne do prawidłowego funkcjonowania serwisu (m.in. zapamiętanie Twojego wyboru w sprawie cookies, bezpieczeństwo oraz logowanie do panelu administracyjnego). Nie wymagają zgody i nie można ich wyłączyć.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analityczne',
                  description:
                    'Narzędzie OpenObserve RUM zbiera anonimowe statystyki o korzystaniu ze strony (odsłony, kliknięcia, błędy, wydajność) oraz nagrania sesji z zamaskowanymi danymi wpisywanymi w formularzach. Dane pomagają nam poprawiać serwis. Włączane wyłącznie za Twoją zgodą.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: { name: 'Nazwa', domain: 'Dostawca', desc: 'Cel', exp: 'Ważność' },
                    body: [
                      {
                        name: '_oo_s',
                        domain: 'OpenObserve (1st party)',
                        desc: 'Identyfikator sesji RUM — łączy zdarzenia w jedną sesję analityczną.',
                        exp: 'Sesja (ok. 15 min – 4 godz.)',
                      },
                      {
                        name: '_oo_* / localStorage',
                        domain: 'OpenObserve (1st party)',
                        desc: 'Pomocnicze klucze RUM (próbkowanie, nagranie sesji).',
                        exp: 'Sesja',
                      },
                    ],
                  },
                },
                {
                  title: 'Więcej informacji',
                  description:
                    'Pełne informacje o administratorze danych i przysługujących Ci prawach znajdziesz w <a href="/polityka-prywatnosci">Polityce prywatności</a>.',
                },
              ],
            },
          },
        },
      },
    })
  }, [rumAppId, rumClientToken])

  return analyticsGranted ? (
    <RumLoader rumAppId={rumAppId} rumClientToken={rumClientToken} />
  ) : null
}

/** Re-open the cookie settings modal (used by the footer link). */
export function openCookieSettings() {
  CC.showPreferences()
}
